<script lang="ts">
  import type { CommunityPlanStatus } from '../admin';

  // The one place the per-community plan tier becomes a coloured pill: label +
  // chip colour per status. Extracted from AdminPreferences (Phase 0) so the
  // affiliate "proof" table wears the exact same badges without duplicating the
  // mapping. `trialEndsAt` is optional — when a trial skool carries its end date
  // (the Plans page) we show "Trial, ends in 3 days"; without one (a referral's
  // skool) it stays a plain "Trial".
  // `compact` is the table form: same colours, short labels, no explanatory tail.
  // A console row lists one badge per community and has to stay scannable, where the
  // Plans page shows a single badge and can afford to spell the tier out.
  let {
    status,
    trialEndsAt = null,
    compact = false,
  }: {
    status: CommunityPlanStatus | string;
    trialEndsAt?: string | null | undefined;
    compact?: boolean;
  } = $props();

  const BADGE_BASE = 'inline-flex items-center rounded-full px-2 py-0.5 text-[0.7rem] font-medium';
  // `free_for_life` is a hand-granted comp, NOT revenue — so it deliberately does not
  // wear the success-green 'paid' chip. Before #156 it derived 'paid' and rendered as
  // "Pro", which is how six giveaway communities on prod read as paying customers.
  const PLAN_LABEL: Record<string, string> = {
    paid: 'Pro',
    free_for_life: 'Free for life',
    free: 'Free — under 100 members',
    trial: 'Trial',
    expired: 'Expired',
    legacy: 'Legacy',
    needs_payment: 'Awaiting payment',
  };
  const PLAN_LABEL_COMPACT: Record<string, string> = {
    paid: 'Pro',
    free_for_life: 'Free for life',
    free: 'Free',
    trial: 'Trial',
    expired: 'Expired',
    legacy: 'Legacy',
    needs_payment: 'Unpaid',
  };
  const PLAN_CLASS: Record<string, string> = {
    paid: 'bg-success/10 text-success',
    free_for_life: 'bg-info/10 text-info',
    free: 'bg-primary/10 text-primary',
    trial: 'bg-primary/10 text-primary',
    expired: 'bg-error/10 text-error',
    legacy: 'bg-base-content/10 text-base-content/72',
    needs_payment: 'bg-warning/10 text-warning',
  };

  // "in 5 days" / "tomorrow" / "today" — the trial badge's relative end date.
  function relFuture(iso: string | null | undefined): string {
    if (!iso) return 'soon';
    const days = Math.ceil((Date.parse(iso) - Date.now()) / 86400000);
    if (Number.isNaN(days)) return 'soon';
    if (days <= 0) return 'today';
    if (days === 1) return 'tomorrow';
    return `in ${days} days`;
  }

  // A missing/unknown status (older API) is a grandfathered skool — never gated.
  const plan = $derived(status || 'legacy');
</script>

<span class="{BADGE_BASE} {PLAN_CLASS[plan] ?? PLAN_CLASS['legacy']}">
  {#if compact}
    {#if plan === 'trial' && trialEndsAt}Trial · {relFuture(trialEndsAt).replace('in ', '')}{:else}{PLAN_LABEL_COMPACT[plan] ?? 'Legacy'}{/if}
  {:else if plan === 'trial' && trialEndsAt}
    Trial, ends {relFuture(trialEndsAt)}
  {:else}
    {PLAN_LABEL[plan] ?? 'Legacy'}
  {/if}
</span>
